import { ENDPOINT } from '~/constants/endpoints';

import { isAbortError, type KyRequestOptions } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type MyUser } from './types';
import { zodMyUserResponseSchema } from './validations';

/**
 * Fetches the current authenticated user's details via the admin API.
 *
 * @param options - Optional request options (e.g., abort signal).
 * @returns The current user data.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/get_my_user_vadmin_user_me_get
 */
export const fetchMyUser = async (options?: KyRequestOptions) => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.USER.GET_ME(), options)
      .json<MyUser>();

    const validated = zodMyUserResponseSchema.safeParse(response);

    if (!validated.success) {
      log.error({
        message: 'fetchMyUser API response has validation errors',
        error: {
          errors: validated.error.issues,
          errorMessage: validated.error.message,
          response,
        },
      });
    }

    return response;
  } catch (error) {
    if (isAbortError(error)) {
      return;
    }

    log.error({
      message: 'Error fetching current user',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.user.fetchMyUser.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.user.fetchMyUser.error.generic, {
            ns: 'data',
          });
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }
};
